import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ImportUI from "./Roles/DIalogBoxes/ImportUI";
import CsvMapping from "./Roles/DIalogBoxes/CsvMapping";
import { 
  parseCsv, 
  validateCsvMapping, 
  importCsvData,
  clearImportResults
} from "../../../redux/customer/customerSlice";
import { toast } from "react-hot-toast";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";

const CustomerImportPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { pathname } = useLocation();
  const fileInputRef = useRef(null);

  const type = pathname.includes("import-location") ? "location" : "department";

  const { parseResult, validateResult, importResult, loadingParams } = useSelector((state) => state.customer);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Mapping
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Mapping state
  const [mappingSelections, setMappingSelections] = useState({});
  const [lastValidatedMapping, setLastValidatedMapping] = useState(null);

  const handleFileChange = (file) => {
    if (file && (file.type === "text/csv" || file.name.endsWith(".csv") || file.name.endsWith(".xlsx"))) {
      dispatch(clearImportResults());
      setSelectedFile(file);
      setMappingSelections({});
      setLastValidatedMapping(null);
    } else {
      toast.error("Please upload a valid CSV or Excel file");
    }
  };

  const onInputChange = (e) => {
    handleFileChange(e.target.files[0]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files[0]);
  };

  const handleNext = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    try {
      const result = await dispatch(parseCsv({ userId, file: selectedFile, type })).unwrap();
      
      if (result.success) {
        // Suggested mappings from API if available
        setMappingSelections(result.data?.suggestedMappings || {});
        setStep(2);
        toast.success("File parsed successfully");
      }
    } catch (error) {
      toast.error(error || "Failed to parse file");
    }
  };

  const handleSelectionChange = (header, value) => {
    setMappingSelections(prev => ({
      ...prev,
      [header]: value
    }));
  };

  const handleValidate = async () => {
    try {
      const response = await dispatch(validateCsvMapping({ userId, mapping: mappingSelections, type })).unwrap();
      if (response.success) {
        setLastValidatedMapping(JSON.stringify(mappingSelections));
        toast.success("Mapping validated");
      }
    } catch (error) {
      toast.error(error || "Validation failed");
    }
  };

  const handleImport = async () => {
    try {
      const response = await dispatch(importCsvData({ 
        userId, 
        mapping: mappingSelections, 
        file: selectedFile,
        type 
      })).unwrap();

      const failedCount = response?.data?.failedCount || 0;
      
      if (failedCount > 0) {
        toast.success("Imported with some errors. Please check the summary.");
      } else {
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
          navigate(-1);
        }, 2000);
      }
    } catch (error) {
      toast.error(error || "Import failed");
    }
  };

  const csvHeaders = parseResult?.data?.csvHeaders || [];
  const dbFields = parseResult?.data?.dbFields || [];
  const sampleRows = parseResult?.data?.sampleData || [];

  const mappingRows = csvHeaders.map(header => ({
    header,
    samples: sampleRows.map(row => row[header]).filter(val => val !== undefined)
  }));

  return (
    <div style={{ paddingLeft: "40px", paddingRight: "40px", paddingTop: "20px" }}>
      {step === 1 ? (
        <ImportUI
          heading={`Import ${type === "location" ? "Locations" : "Departments"}`}
          description={`Upload a CSV file to import ${type === "location" ? "locations" : "departments"}.`}
           downloadHref={`${type === "location" ? "/samples/location-import-sample.csv":"/samples/department-import-sample.csv"}`}
          downloadName={`${type === "location" ? "location-import-sample.csv":"department-import-sample.csv"}`}
          description="Upload a CSV file to import"
            downloadLabel = "Download Sample"
          onBack={() => navigate(-1)}
          onCancel={() => navigate(-1)}
          supportedTypes={[".csv"]}
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          inputRef={fileInputRef}
          onInputChange={onInputChange}
          openFileDialog={() => fileInputRef.current?.click()}
          selectedFile={selectedFile}
          onNext={handleNext}
          nextDisabled={!selectedFile || loadingParams.isParsing}
          nextLabel={loadingParams.isParsing ? "Parsing..." : "Next"}
          isDragging={isDragging}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
        />
      ) : (
        <CsvMapping
          mappingRows={mappingRows}
          mappingSelections={mappingSelections}
          attributeOptions={dbFields}
          onSelectionChange={handleSelectionChange}
          onCancel={() => setStep(1)}
          onValidate={handleValidate}
          onImport={handleImport}
          validateResult={validateResult}
          importResult={importResult}
          lastValidatedMapping={lastValidatedMapping}
          isValidating={loadingParams.isValidating}
          isImporting={loadingParams.isImporting}
          showValidate={false}
        />
      )}

      {showSuccessPopup && createPortal(
        <div className="fixed inset-0 bg-black/40 z-[10000] flex items-center justify-center backdrop-blur-sm">
          <PopupCard
            type="success"
            title="Import Successful"
            message={`${type === "location" ? "Locations" : "Departments"} have been imported successfully.`}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomerImportPage;
